import type { NavItem } from "@/config/navigation";
import {
  FAMILY_HUB_NESTED_NAV,
  type FamilyHubNestedItemId,
  type FamilyHubNestedSectionId,
} from "@/components/family/familyHubNestedNavConfig";

export const FAMILY_HUB_NAV_BASE = "/family";

export function buildFamilyHubHref(
  basePath: string,
  section: FamilyHubNestedSectionId,
  item: FamilyHubNestedItemId
): string {
  const params = new URLSearchParams();
  params.set("section", section);
  params.set("item", item);
  return `${basePath}?${params.toString()}`;
}

/**
 * Build Family Hub Submenu.
 */
export function buildFamilyHubSubmenu(basePath: string = FAMILY_HUB_NAV_BASE): NavItem[] {
  return FAMILY_HUB_NESTED_NAV.map((group) => {
    const [firstItem] = group.items;
    const groupHref = buildFamilyHubHref(basePath, group.section, firstItem.id);

    if (group.items.length === 1) {
      return {
        title: group.label,
        label: "",
        icon: group.icon,
        href: groupHref,
      };
    }

    return {
      title: group.label,
      label: "",
      icon: group.icon,
      href: groupHref,
      submenu: group.items.map((item) => ({
        title: item.label,
        label: "",
        icon: group.icon,
        href: buildFamilyHubHref(basePath, group.section, item.id),
      })),
    };
  });
}

export function appendDevPreviewParam(href: string): string {
  if (!import.meta.env.DEV) return href;
  const { pathname: hrefPath, search: hrefSearch } = parseNavHref(href);
  const params = new URLSearchParams(hrefSearch ? hrefSearch.slice(1) : "");
  params.set("preview", "1");
  return `${hrefPath}?${params.toString()}`;
}

export function withDevPreviewNavItems(items: NavItem[]): NavItem[] {
  return items.map((item) => ({
    ...item,
    href: appendDevPreviewParam(item.href),
    submenu: item.submenu ? withDevPreviewNavItems(item.submenu) : undefined,
  }));
}

export function parseNavHref(href: string): { pathname: string; search: string } {
  const [pathname, search = ""] = href.split("?");
  return { pathname, search: search ? `?${search}` : "" };
}

export function isFamilyHubRoute(pathname: string): boolean {
  return pathname === FAMILY_HUB_NAV_BASE || pathname.startsWith(`${FAMILY_HUB_NAV_BASE}/`);
}

/**
 * Is Nav Href Active.
 */
export function isNavHrefActive(href: string, pathname: string, search: string): boolean {
  const { pathname: hrefPath, search: hrefSearch } = parseNavHref(href);
  const isFamilyHubRoot = hrefPath === FAMILY_HUB_NAV_BASE;

  if (hrefSearch) {
    if (pathname !== hrefPath) {
      return false;
    }
    const hrefParams = new URLSearchParams(hrefSearch.slice(1));
    const currentParams = new URLSearchParams(search);
    for (const [key, value] of hrefParams.entries()) {
      if (currentParams.get(key) !== value) {
        return false;
      }
    }
    return true;
  }

  if (pathname === hrefPath) {
    return !isFamilyHubRoot;
  }

  return !isFamilyHubRoot && hrefPath !== "/" && pathname.startsWith(`${hrefPath}/`);
}

export function isNavItemBranchActive(item: NavItem, pathname: string, search: string): boolean {
  if (isNavHrefActive(item.href, pathname, search)) {
    return true;
  }
  return item.submenu?.some((child) => isNavItemBranchActive(child, pathname, search)) ?? false;
}

export function isNavSubmenuBranchActive(
  items: NavItem[],
  pathname: string,
  search: string
): boolean {
  return items.some((item) => isNavItemBranchActive(item, pathname, search));
}
